import { ChevronLeft, ChevronRight, Clock, Phone, Plus, Search, UserCheck, UserX, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEmployees, getDeletedEmployees, restoreEmployee } from '../../../api/employee.api';
import styles from '../../../assets/scss/pages/EmployeeList.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatPhoneNumber } from '../../../utils/formatters';

const PAGE_SIZE = 20;

const AVATAR_COLORS = ['#0098F6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

function getAvatarColor(name) {
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function normalize(str) {
    return (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, '')
        .toLowerCase();
}

export default function EmployeeList() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.EMPLOYEE.VIEW);
    const canDetail = hasPermission(PERMISSIONS.EMPLOYEE.DETAIL);
    const canCreate = hasPermission(PERMISSIONS.EMPLOYEE.CREATE);
    const canDelete = hasPermission(PERMISSIONS.EMPLOYEE.DELETE);

    const [employees, setEmployees] = useState([]);
    const [deletedEmployees, setDeletedEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [showDeleted, setShowDeleted] = useState(false);
    const [toastMsg, setToastMsg] = useState(null);

    const fetchEmployees = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const [activeRes, deletedRes] = await Promise.all([
                getEmployees(),
                getDeletedEmployees(),
            ]);
            setEmployees(activeRes.data.employees || []);
            setDeletedEmployees(deletedRes.data.employees || []);
        } catch (err) {
            console.error('Error fetching employees:', err);
            setEmployees([]);
            setDeletedEmployees([]);
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const handleRestore = async (emp) => {
        if (!window.confirm(`Khôi phục nhân viên "${emp.name}"?`)) return;
        try {
            await restoreEmployee(emp.id);
            setToastMsg({ type: 'success', text: 'Khôi phục thành công' });
            setTimeout(() => setToastMsg(null), 1500);
            fetchEmployees();
            setShowDeleted(false);
        } catch {
            setToastMsg({ type: 'error', text: 'Khôi phục thất bại' });
            setTimeout(() => setToastMsg(null), 2500);
        }
    };

    // Filter
    const currentList = showDeleted ? deletedEmployees : employees;
    const filtered = currentList.filter((emp) => {
        if (!searchText.trim()) return true;
        const q = normalize(searchText);
        return normalize(emp.name).includes(q) || normalize(emp.phone).includes(q);
    });

    // Pagination
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const startIdx = (safePage - 1) * PAGE_SIZE + 1;
    const endIdx = Math.min(safePage * PAGE_SIZE, filtered.length);

    // KPI
    const totalActive = employees.filter((e) => e.status === true || e.status === 'active').length;
    const totalInactive = employees.length - totalActive;

    // Page buttons
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
        if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
        return pages;
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.breadcrumb}>Quản lý &rsaquo; Nhân viên</div>
                    <h1 className={styles.title}>Nhân viên</h1>
                    <p className={styles.subtitle}>
                        {showDeleted
                            ? `${deletedEmployees.length} nhân viên đã xoá`
                            : `${employees.length} nhân viên`}
                    </p>
                </div>
                <div className={styles.headerActions}>
                    {canDelete && (
                        <button
                            className={`${styles.btn} ${styles.btnOutline}`}
                            onClick={() => { setShowDeleted(!showDeleted); setPage(1); setSearchText(''); }}
                        >
                            {showDeleted ? 'Quay lại' : 'Đã xoá'}
                        </button>
                    )}
                    {canCreate && !showDeleted && (
                        <button
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            onClick={() => navigate('/admin/employees/create')}
                        >
                            <Plus /> Thêm nhân viên
                        </button>
                    )}
                </div>
            </div>

            {/* KPI */}
            {!showDeleted && (
                <div className={styles.kpis}>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>Tổng nhân viên</span>
                            <span className={styles.kpiValue}>{employees.length}</span>
                        </div>
                        <div className={`${styles.kpiIcon} ${styles.kpiIconBlue}`}><Users /></div>
                    </div>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>Đang làm việc</span>
                            <span className={styles.kpiValue}>{totalActive}</span>
                        </div>
                        <div className={`${styles.kpiIcon} ${styles.kpiIconGreen}`}><UserCheck /></div>
                    </div>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>Ngừng làm việc</span>
                            <span className={styles.kpiValue}>{totalInactive}</span>
                        </div>
                        <div className={`${styles.kpiIcon} ${styles.kpiIconOrange}`}><UserX /></div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className={styles.filters}>
                <div className={styles.searchWrap}>
                    <Search className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        value={searchText}
                        onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
                        placeholder="Tìm theo tên hoặc SĐT..."
                    />
                </div>
                <span className={styles.filterCount}>
                    {filtered.length} kết quả
                </span>
            </div>

            {/* Table */}
            <div className={styles.tableWrapper}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} />
                        Đang tải danh sách...
                    </div>
                ) : paged.length === 0 ? (
                    <div className={styles.empty}>
                        {showDeleted ? 'Không có nhân viên đã xoá' : 'Không tìm thấy nhân viên nào'}
                    </div>
                ) : (
                    <>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Nhân viên</th>
                                    <th>Số điện thoại</th>
                                    <th>Ca làm việc</th>
                                    <th>Trạng thái</th>
                                    <th>Vai trò</th>
                                    {showDeleted && canDelete && <th style={{ width: 100 }}>Thao tác</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map((emp) => {
                                    const isActive = emp.status === true || emp.status === 'active';
                                    const isAdmin = emp.user?.role === 'admin';
                                    return (
                                        <tr
                                            key={emp.id}
                                            className={!showDeleted && canDetail ? styles.rowClickable : undefined}
                                            onClick={() => {
                                                if (showDeleted || !canDetail) return;
                                                navigate(`/admin/employees/${emp.id}`);
                                            }}
                                        >
                                            <td>
                                                <div className={styles.employeeCell}>
                                                    <div
                                                        className={styles.avatar}
                                                        style={{ background: getAvatarColor(emp.name) }}
                                                    >
                                                        {(emp.name || '?')[0].toUpperCase()}
                                                    </div>
                                                    <span className={styles.employeeName}>{emp.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.phoneCell}>
                                                    <Phone size={14} />
                                                    {emp.phone ? formatPhoneNumber(emp.phone) : 'Chưa có'}
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.shiftCell}>
                                                    {emp.shiftType?.name || 'Chưa có ca'}
                                                    {emp.shiftType?.startTime && (
                                                        <div className={styles.shiftTime}>
                                                            <Clock size={11} style={{ marginRight: 3, verticalAlign: -1 }} />
                                                            {emp.shiftType.startTime} - {emp.shiftType.endTime || 'x'}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {!showDeleted && (
                                                    <span className={`${styles.statusBadge} ${isActive ? styles.statusActive : styles.statusInactive}`}>
                                                        {isActive ? 'Đang làm' : 'Ngừng làm'}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {isAdmin && <span className={styles.roleBadge}>Boss</span>}
                                            </td>
                                            {showDeleted && canDelete && (
                                                <td>
                                                    <button
                                                        className={`${styles.btn} ${styles.btnOutline}`}
                                                        style={{ height: 34, padding: '0 14px', fontSize: 13 }}
                                                        onClick={(e) => { e.stopPropagation(); handleRestore(emp); }}
                                                    >
                                                        Khôi phục
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {filtered.length > PAGE_SIZE && (
                            <div className={styles.pagination}>
                                <span className={styles.paginationInfo}>
                                    {startIdx}–{endIdx} / {filtered.length}
                                </span>
                                <div className={styles.paginationControls}>
                                    <button
                                        className={styles.pageBtn}
                                        disabled={safePage <= 1}
                                        onClick={() => setPage(safePage - 1)}
                                    >
                                        <ChevronLeft />
                                    </button>
                                    {getPageNumbers().map((p, i) =>
                                        p === '...' ? (
                                            <span key={`dot-${i}`} className={styles.pageDots}>...</span>
                                        ) : (
                                            <button
                                                key={p}
                                                className={`${styles.pageBtn}${p === safePage ? ` ${styles.pageBtnActive}` : ''}`}
                                                onClick={() => setPage(p)}
                                            >
                                                {p}
                                            </button>
                                        ),
                                    )}
                                    <button
                                        className={styles.pageBtn}
                                        disabled={safePage >= totalPages}
                                        onClick={() => setPage(safePage + 1)}
                                    >
                                        <ChevronRight />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Toast */}
            {toastMsg && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 2000,
                    background: 'rgba(0,0,0,0.3)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 20, padding: '40px 48px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    }}>
                        <div style={{
                            width: 60, height: 60, borderRadius: '50%',
                            background: toastMsg.type === 'success' ? '#22C55E' : '#EF4444',
                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 28, fontWeight: 700,
                        }}>
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A' }}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
